import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Heart, BookOpen, Sparkles, ArrowUpRight, Calendar, Send, Lock } from "lucide-react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import SectionLabel from "@/components/SectionLabel";
import ShareButtons from "@/components/ShareButtons";
import NewsletterSignup from "@/components/NewsletterSignup";

const HERO =
  "https://images.unsplash.com/photo-1762541115443-d83816da748c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHx3ZWxsbmVzcyUyMG1lZGl0YXRpb24lMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzc3Mjg2NTh8MA&ixlib=rb-4.1.0&q=85";

const FALLBACK_COVER =
  "https://images.unsplash.com/photo-1758637612400-09e155a1278c?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

function BlogCoverImg({ src, alt, className }) {
  const initial = imgUrl(src) || FALLBACK_COVER;
  return (
    <img
      src={initial}
      alt={alt || "Blog cover"}
      loading="lazy"
      className={`w-full ${className || ""}`}
      onError={(e) => {
        if (e.currentTarget.src !== FALLBACK_COVER) {
          e.currentTarget.src = FALLBACK_COVER;
        }
      }}
      data-testid="blog-cover-img"
    />
  );
}

export default function Inspire() {
  const [articles, setArticles] = useState([]);
  const [latestWeekly, setLatestWeekly] = useState(null);
  const [categories, setCategories] = useState(["All"]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api
      .get("/public/blog/latest-weekly")
      .then((r) => setLatestWeekly(r.data.article))
      .catch(() => {});
    api
      .get("/public/blog/categories")
      .then((r) => setCategories(["All", ...(r.data.categories || [])]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (category && category !== "All") params.category = category;
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/public/blog", { params })
        .then((r) => setArticles(r.data.articles || []))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [search, category]);

  return (
    <div data-testid="inspire-page">
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-end overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-50"
          style={{ backgroundImage: `url(${HERO})` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(5,3,8,0.5) 0%, rgba(5,3,8,0.95) 100%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-20 w-full">
          <div className="max-w-3xl space-y-6 fade-up">
            <SectionLabel number="03" label="The Mission" />
            <h1 className="font-serif font-light tracking-tighter text-[2.6rem] sm:text-6xl lg:text-8xl leading-[0.95]">
              Inspire Da
              <br />
              <em className="italic" style={{ color: "var(--gold)" }}>
                Streets.
              </em>
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl leading-relaxed">
              Our non-profit raises the floor for artist mental health, tech
              training, and youth empowerment — built for the kids who sound
              like the future and the artists keeping them lit.
            </p>
            <div className="flex items-center gap-3 pt-1" data-testid="inspire-share">
              <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Share</span>
              <ShareButtons
                shareUrl={`${window.location.origin}/api/og/page/inspire`}
                canonicalUrl={`${window.location.origin}/inspire`}
                text="Inspire Da Streets — Wellness & Empowerment by Nina Capone"
                testidPrefix="inspire-share"
              />
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-3 gap-6">
          <Pillar
            icon={<Heart size={20} />}
            label="Mental Health"
            copy="Therapy stipends, peer-support circles, and resource directories specifically for working artists."
          />
          <Pillar
            icon={<BookOpen size={20} />}
            label="Tech Training"
            copy="Code The Block — our free 8-week web + AI fundamentals program for Philly youth."
          />
          <Pillar
            icon={<Sparkles size={20} />}
            label="Empowerment"
            copy="Leadership cohorts and creative-business labs that hand the mic, the mastering, and the keys."
          />
        </div>
      </section>

      {/* AGORAPHOBIA — FOUNDER'S STORY */}
      <section
        id="agoraphobia"
        className="relative py-24 border-t border-white/5 overflow-hidden scroll-mt-24"
        data-testid="agoraphobia-story"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 70% 90% at 100% 0%, rgba(232,180,184,0.10), transparent 60%), radial-gradient(ellipse 50% 70% at 0% 100%, rgba(74,14,62,0.35), transparent 60%)",
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 space-y-6">
              <div className="flex items-center gap-3">
                <Heart size={16} style={{ color: "var(--gold)" }} />
                <span className="eyebrow">Founder&rsquo;s Story · A Personal Note</span>
              </div>
              <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
                The Stage Was{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  My Sanctuary.
                </em>{" "}
                And Sometimes My{" "}
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Trigger.
                </em>
              </h2>
              <p className="text-white/55 text-sm leading-relaxed">
                A first-person dispatch from Nina on touring with agoraphobia — and why
                this non-profit exists.
              </p>
            </div>

            <div className="lg:col-span-8 space-y-6 font-serif text-lg lg:text-xl text-white/85 leading-[1.65]">
              <p>
                For most of my career, I performed with a secret. The same body that
                commanded a stage in front of a thousand people would lock up walking
                into a hotel lobby alone. Crowded green rooms, airport terminals,
                arena tunnels — they would close in on me until I couldn&rsquo;t breathe.
                The world calls it{" "}
                <em
                  className="italic"
                  style={{ color: "var(--gold)" }}
                >
                  agoraphobia
                </em>
                . I called it the trap.
              </p>
              <p>
                I&rsquo;d be on tour, sold out, and still need an hour in the bathroom
                before soundcheck just to settle my chest. I learned to pre-walk
                venues at 6 AM when they were empty. I learned which exits were
                where. I learned to fake &ldquo;just tired&rdquo; when really I was rationing
                the energy it took to be in public at all. Most artists you watch
                on stage are fighting some version of this. We just don&rsquo;t talk
                about it.
              </p>
              <p>
                <strong style={{ color: "var(--gold)" }}>
                  That silence is what kills us.
                </strong>{" "}
                It&rsquo;s why touring artists die in hotel rooms. It&rsquo;s why the kid from
                Philly who can rap better than anyone in his neighborhood freezes
                the first time he has to fly to a session. The condition isn&rsquo;t
                weakness — it&rsquo;s a nervous system that learned, somewhere, that
                open spaces aren&rsquo;t safe. And it can be treated. It can be managed.
                It can be lived through. But only if we name it out loud.
              </p>
              <p>
                That&rsquo;s the heart of{" "}
                <strong style={{ color: "var(--gold)" }}>Inspire Da Streets</strong>
                . I&rsquo;m building the resource I needed at 22 — therapy stipends for
                artists, peer-support circles for tour managers and crews, panic-
                attack training for venue staff, and a public conversation that
                says <em>this happens to professionals at the top of the game,
                and you&rsquo;re not broken for living it.</em>
              </p>
              <p>
                If you&rsquo;re touring right now and reading this from a hotel bathroom
                floor — I see you. The stage will hold. So will you.
              </p>
              <p
                className="pt-2 not-italic font-sans text-sm uppercase tracking-[0.3em]"
                style={{ color: "var(--gold)" }}
              >
                — Nina Capone
              </p>
            </div>
          </div>

          {/* AWARENESS STAT STRIP */}
          <div className="mt-20 grid sm:grid-cols-3 gap-6 lg:gap-10">
            <StatCard
              k="73%"
              label="Of Touring Musicians"
              copy="report symptoms of anxiety or panic disorder during active touring (Help Musicians, 2022)."
            />
            <StatCard
              k="2 In 100"
              label="Adults Live With Agoraphobia"
              copy="and the rate is markedly higher among performing artists and high-visibility creatives (NIMH)."
            />
            <StatCard
              k="< 1 In 3"
              label="Will Seek Treatment"
              copy="Stigma, cost, and the myth of the “unbreakable performer” keep most of us suffering in silence."
            />
          </div>

          {/* CTA */}
          <div className="mt-16 surface p-8 lg:p-10 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8 space-y-3">
              <p className="eyebrow">Bring The Awareness Forward</p>
              <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light leading-tight">
                Share your story. Or read someone else&rsquo;s.{" "}
                <em style={{ color: "var(--gold)" }} className="italic">
                  Either one breaks the silence.
                </em>
              </h3>
              <p className="text-white/65 text-base leading-relaxed max-w-2xl">
                Every Monday we drop a new wellness essay — sometimes Nina&rsquo;s,
                sometimes a guest artist&rsquo;s, sometimes a clinician&rsquo;s. Subscribe
                to the Inner Circle and never miss a dispatch.
              </p>
            </div>
            <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
              <a
                href="#share-story"
                className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-xs"
                data-testid="agoraphobia-share-story"
              >
                Share Your Story
                <ArrowUpRight size={14} />
              </a>
              <a
                href="#weekly"
                className="btn-ghost-gold inline-flex items-center justify-center gap-2 px-6 py-3 text-xs"
                data-testid="agoraphobia-read-weekly"
              >
                Read This Week&rsquo;s Drop
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* SHARE YOUR STORY — FORM */}
      <ShareStorySection />

      {/* NEWSLETTER · INNER CIRCLE */}
      <NewsletterSignup
        source="inspire-mid"
        testId="inspire-newsletter-signup"
        heading={
          <>
            Never miss a{" "}
            <em className="italic" style={{ color: "var(--gold)" }}>
              Monday Drop.
            </em>
          </>
        }
        subheading="Every Monday, a new wellness essay — sometimes Nina's, sometimes a guest artist's, sometimes a clinician's. Subscribe to the Inner Circle and we'll send it straight to you."
      />

      {/* LATEST WEEKLY DROP */}
      {latestWeekly && (
        <section
          className="py-16 border-t border-white/5 scroll-mt-24"
          id="weekly"
          data-testid="latest-weekly-drop"
        >
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <Link
              to={`/blog/${latestWeekly.slug}`}
              className="surface relative overflow-hidden block group"
            >
              <div
                className="absolute inset-0 pointer-events-none opacity-50"
                style={{
                  background:
                    "radial-gradient(ellipse 50% 80% at 100% 0%, rgba(232,180,184,0.18), transparent 60%), radial-gradient(ellipse 60% 80% at 0% 100%, rgba(74,14,62,0.4), transparent 60%)",
                }}
              />
              <div className="relative grid lg:grid-cols-12 gap-10 p-10 lg:p-14 items-center">
                <div className="lg:col-span-8 space-y-5">
                  <div className="flex flex-wrap items-center gap-3">
                    <span
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] border"
                      style={{
                        color: "var(--gold)",
                        borderColor: "rgba(232,180,184,0.5)",
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full live-dot"
                        style={{ background: "var(--gold)" }}
                      />
                      This Week&rsquo;s Drop
                    </span>
                    <span className="eyebrow">{latestWeekly.category}</span>
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] text-white/45">
                      <Calendar size={10} />
                      {new Date(latestWeekly.created_at).toLocaleDateString(
                        undefined,
                        { month: "long", day: "numeric", year: "numeric" }
                      )}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl sm:text-3xl lg:text-5xl tracking-tighter font-light leading-[1.05] group-hover:text-[color:var(--gold)] transition-colors">
                    {latestWeekly.title}
                  </h3>
                  <p className="font-serif italic text-lg lg:text-xl text-white/75 max-w-2xl leading-relaxed">
                    {latestWeekly.excerpt}
                  </p>
                  {latestWeekly.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {latestWeekly.tags.slice(0, 5).map((t) => (
                        <span
                          key={t}
                          className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 border border-white/10 text-white/60"
                        >
                          #{t}
                        </span>
                      ))}
                    </div>
                  )}
                  <span
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] pt-2 group-hover:gap-3 transition-all"
                    style={{ color: "var(--gold)" }}
                  >
                    Read This Week&rsquo;s Article
                    <ArrowUpRight size={14} />
                  </span>
                </div>
                <div className="lg:col-span-4 hidden lg:flex items-center justify-center">
                  <div className="relative">
                    <div
                      className="absolute -inset-4 rounded-full opacity-40 blur-2xl"
                      style={{ background: "var(--gold)" }}
                    />
                    <div
                      className="relative w-44 h-44 rounded-full surface flex items-center justify-center border-2"
                      style={{ borderColor: "rgba(232,180,184,0.4)" }}
                    >
                      <div className="text-center">
                        <Heart
                          size={32}
                          strokeWidth={1.2}
                          style={{ color: "var(--gold)" }}
                          fill="var(--gold)"
                          fillOpacity={0.2}
                          className="mx-auto mb-2"
                        />
                        <p
                          className="font-serif italic text-xl tracking-tighter"
                          style={{ color: "var(--gold)" }}
                        >
                          Weekly
                        </p>
                        <p className="text-[9px] uppercase tracking-[0.3em] text-white/55 mt-1">
                          Mondays
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* WELLNESS BLOG */}
      <section className="py-20 border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-8 mb-12 items-end">
            <div className="lg:col-span-6">
              <SectionLabel label="Wellness Blog" />
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light">
                Read. Reflect.
                <br />
                <em className="italic" style={{ color: "var(--gold)" }}>
                  Rebuild.
                </em>
              </h2>
            </div>
            <div className="lg:col-span-6 space-y-4">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40"
                />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search resources, tools, stories…"
                  className="pl-11 bg-transparent border border-white/15 rounded-none h-12 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
                  data-testid="blog-search-input"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] border transition-colors ${
                      category === c
                        ? "border-[color:var(--gold)] text-[color:var(--gold)]"
                        : "border-white/10 text-white/55 hover:border-white/30"
                    }`}
                    data-testid={`blog-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <p className="text-white/40 italic font-serif">Loading…</p>
          ) : articles.length === 0 ? (
            <p
              className="text-white/40 italic font-serif"
              data-testid="blog-empty-state"
            >
              No articles match. Try another search.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {articles.map((a) => (
                <Link
                  key={a.id}
                  to={`/blog/${a.slug}`}
                  className="surface group block"
                  data-testid={`blog-card-${a.slug}`}
                >
                  <BlogCoverImg
                    src={a.cover_image}
                    alt={a.title}
                    className="aspect-[4/3] object-cover"
                  />
                  <div className="p-7 space-y-4">
                    <p className="eyebrow">{a.category}</p>
                    <h3 className="font-serif text-2xl tracking-tighter font-medium leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                      {a.title}
                    </h3>
                    <p className="text-sm text-white/55 leading-relaxed line-clamp-3">
                      {a.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] pt-2" style={{ color: "var(--gold)" }}>
                      Read <ArrowUpRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Pillar({ icon, label, copy }) {
  return (
    <div className="surface p-8 lg:p-10">
      <div style={{ color: "var(--gold)" }} className="mb-6">
        {icon}
      </div>
      <h3 className="font-serif text-3xl tracking-tighter font-light mb-3">
        {label}
      </h3>
      <p className="text-sm text-white/55 leading-relaxed">{copy}</p>
    </div>
  );
}

function StatCard({ k, label, copy }) {
  return (
    <div className="surface p-7 lg:p-8 space-y-3">
      <div
        className="font-serif tracking-tighter text-5xl lg:text-6xl leading-none"
        style={{ color: "var(--gold)" }}
      >
        {k}
      </div>
      <div className="text-[10px] uppercase tracking-[0.28em] text-white/55 font-sans">
        {label}
      </div>
      <p className="text-sm text-white/60 leading-relaxed">{copy}</p>
    </div>
  );
}

const RELATIONSHIP_OPTS = [
  { value: "artist", label: "Artist / Performer" },
  { value: "fan", label: "Fan / Listener" },
  { value: "clinician", label: "Clinician / Therapist" },
  { value: "family", label: "Family / Friend" },
  { value: "other", label: "Other" },
];

function ShareStorySection() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    story_title: "",
    relationship: "",
    story: "",
    anonymous: false,
    publish_consent: false,
  });

  const update = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value ?? e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.story.trim().length < 20) {
      toast.error("Tell us a little more — at least a few sentences.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Drop empty optional fields so backend treats them as null
      if (!payload.name) delete payload.name;
      if (!payload.email) delete payload.email;
      if (!payload.story_title) delete payload.story_title;
      if (!payload.relationship) delete payload.relationship;
      if (payload.anonymous) {
        delete payload.name;
        delete payload.email;
      }
      await api.post("/public/share-story", payload);
      setSubmitted(true);
      toast.success("Your story has been received. Thank you for trusting us.");
    } catch (err) {
      toast.error(formatApiError(err) || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="share-story"
      className="relative py-24 border-t border-white/5 overflow-hidden scroll-mt-24"
      data-testid="share-story-section"
    >
      <div
        className="absolute inset-0 pointer-events-none opacity-50"
        style={{
          background:
            "radial-gradient(ellipse 60% 80% at 0% 0%, rgba(232,180,184,0.10), transparent 60%), radial-gradient(ellipse 70% 80% at 100% 100%, rgba(74,14,62,0.30), transparent 60%)",
        }}
      />
      <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-5 space-y-5">
            <SectionLabel number="Your Voice" label="Share Your Story" />
            <h2 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.92]">
              Break the{" "}
              <em className="italic" style={{ color: "var(--gold)" }}>
                silence.
              </em>
            </h2>
            <p className="text-white/65 text-base leading-relaxed">
              If Nina&rsquo;s story moved something in you — or if you&rsquo;re carrying
              one of your own — we want to hear it. Submit it here. Read it
              once, twice. Decide what feels right. We&rsquo;ll only ever publish
              with your written approval.
            </p>
            <div className="flex items-center gap-2 text-xs text-white/50">
              <Lock size={13} style={{ color: "var(--gold)" }} />
              <span>Private by default · You control what becomes public.</span>
            </div>
          </div>

          <div className="lg:col-span-7">
            {submitted ? (
              <div
                className="surface p-10 lg:p-14 text-center space-y-5"
                data-testid="share-story-success"
              >
                <Heart
                  size={42}
                  strokeWidth={1}
                  style={{ color: "var(--gold)" }}
                  fill="var(--gold)"
                  fillOpacity={0.18}
                  className="mx-auto"
                />
                <h3 className="font-serif text-3xl lg:text-4xl tracking-tighter font-light">
                  Thank you for trusting{" "}
                  <em className="italic" style={{ color: "var(--gold)" }}>
                    us with this.
                  </em>
                </h3>
                <p className="text-white/65 text-base max-w-md mx-auto leading-relaxed">
                  Nina and the Inspire Da Streets team will personally read
                  what you sent. If you marked it okay-to-publish, we may
                  reach out about featuring it (always with your final
                  approval).
                </p>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({
                        name: "",
                        email: "",
                        story_title: "",
                        relationship: "",
                        story: "",
                        anonymous: false,
                        publish_consent: false,
                      });
                    }}
                    className="text-xs uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] transition"
                    data-testid="share-story-reset"
                  >
                    Submit another
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                className="surface p-7 lg:p-10 space-y-6"
                data-testid="share-story-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <StoryField label="Your Name (optional)" id="ss-name">
                    <Input
                      id="ss-name"
                      value={form.name}
                      onChange={update("name")}
                      disabled={form.anonymous}
                      placeholder={form.anonymous ? "— Anonymous —" : ""}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 disabled:opacity-40"
                      data-testid="share-story-name"
                    />
                  </StoryField>
                  <StoryField label="Email (optional)" id="ss-email">
                    <Input
                      id="ss-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      disabled={form.anonymous}
                      placeholder={form.anonymous ? "— withheld —" : ""}
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0 disabled:opacity-40"
                      data-testid="share-story-email"
                    />
                  </StoryField>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <StoryField label="Story Title (optional)" id="ss-title">
                    <Input
                      id="ss-title"
                      value={form.story_title}
                      onChange={update("story_title")}
                      placeholder='e.g. "The hotel bathroom floor"'
                      className="bg-transparent border-0 border-b border-white/15 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] px-0"
                      data-testid="share-story-title"
                    />
                  </StoryField>
                  <StoryField label="You Are A..." id="ss-rel">
                    <select
                      id="ss-rel"
                      value={form.relationship}
                      onChange={update("relationship")}
                      className="w-full bg-transparent border-0 border-b border-white/15 rounded-none focus:outline-none focus:border-[color:var(--gold)] px-0 py-2 text-sm text-white"
                      data-testid="share-story-relationship"
                    >
                      <option value="" className="bg-[#0a050d]">
                        — Select —
                      </option>
                      {RELATIONSHIP_OPTS.map((o) => (
                        <option
                          key={o.value}
                          value={o.value}
                          className="bg-[#0a050d]"
                        >
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </StoryField>
                </div>

                <StoryField label="Your Story" id="ss-story" required>
                  <Textarea
                    id="ss-story"
                    required
                    rows={8}
                    minLength={20}
                    maxLength={8000}
                    value={form.story}
                    onChange={update("story")}
                    placeholder="Take your time. There's no right way to tell it."
                    className="bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] resize-none"
                    data-testid="share-story-body"
                  />
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/35 text-right">
                    {form.story.length} / 8000
                  </div>
                </StoryField>

                <div className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={form.anonymous}
                      onCheckedChange={(v) =>
                        setForm((f) => ({ ...f, anonymous: !!v }))
                      }
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-[color:var(--gold)] data-[state=checked]:border-[color:var(--gold)] data-[state=checked]:text-black"
                      data-testid="share-story-anonymous"
                    />
                    <span className="text-sm text-white/75 leading-relaxed">
                      Submit anonymously — withhold my name and email from the
                      team.
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox
                      checked={form.publish_consent}
                      onCheckedChange={(v) =>
                        setForm((f) => ({ ...f, publish_consent: !!v }))
                      }
                      className="mt-0.5 border-white/30 data-[state=checked]:bg-[color:var(--gold)] data-[state=checked]:border-[color:var(--gold)] data-[state=checked]:text-black"
                      data-testid="share-story-consent"
                    />
                    <span className="text-sm text-white/75 leading-relaxed">
                      You may consider featuring my story (anonymously or with
                      attribution — I&rsquo;ll confirm before anything is published).
                    </span>
                  </label>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">
                    {form.anonymous ? "Anonymous" : "On the record"}
                  </p>
                  <Button
                    type="submit"
                    disabled={submitting || form.story.trim().length < 20}
                    className="btn-gold inline-flex items-center gap-2 px-7 py-3 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="share-story-submit"
                  >
                    {submitting ? "Sending..." : "Send My Story"}
                    <Send size={14} />
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function StoryField({ label, id, required, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="eyebrow">
        {label}
        {required && <span style={{ color: "var(--gold)" }}> *</span>}
      </Label>
      {children}
    </div>
  );
}
